/**
 * Stronger variant of "x as unknown as Y", which guards that x is still the expected type.
 *
 * When you use `as unknown as`, you lose all type safety in that place, but sometimes
 * there is no better way. We can at least return some safety by ensuring that the
 * type before the assertion did not change. See the example below.
 *
 * This is safer than `as unknown as`, but still type unsafe operation.
 *
 * @example
 * ```
 * function tellMeAboutNode(node: NodeId) {
 *   ... // lots of code
 *
 *   // In later refactoring I can change the `node` argument to `Node` object type
 *   // and break the following lines.
 *   // USAFE
 *   const nodeData1 = getNodeDataBySn(node as unknown as NodeSn);
 *   // Safer, will error if `node` type changes
 *   const nodeData2 = getNodeDataBySn(unsafeAssert<NodeId, NodeSn>(node));
 * }
 *
 * ```
 */
export const unsafeAssert = <FromType, ToType>(value: FromType): ToType =>
  value as unknown as ToType;
